import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Link,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link as RouterLink } from "@tanstack/react-router"

import { LicenseService } from "../../api/license"
import type { UserPublic } from "../../client"
import { isSuperAdmin } from "../../utils/roles"

function getBannerCopy(status: string) {
  switch (status) {
    case "unlicensed":
      return {
        type: "warning" as const,
        title: "Brak aktualnego potwierdzenia licencji",
        description:
          "Backend WMS nie może potwierdzić aktywnej licencji. Sprawdź połączenie z LMS, dane instalacji i status licencji.",
      }
    case "revoked":
      return {
        type: "error" as const,
        title: "Licencja zostala cofnieta",
        description:
          "Dostawca cofnal licencje. Czesc funkcji WMS moze byc niedostepna do czasu ponownej aktywacji.",
      }
    case "expired_grace":
      return {
        type: "warning" as const,
        title: "Licencja wygasla",
        description:
          "Licencja wygasla, ale system dziala jeszcze w okresie przejsciowym. Nalezy ja odnowic, aby uniknac ograniczen.",
      }
    case "expired_limited":
      return {
        type: "warning" as const,
        title: "Licencja wygasla z ograniczeniami",
        description:
          "Licencja wygasla i czesc funkcji WMS jest juz ograniczona. Wymagane jest odnowienie licencji.",
      }
    case "expired_critical":
      return {
        type: "error" as const,
        title: "Licencja w trybie krytycznym",
        description:
          "Licencja pozostaje wygasla przez dluzszy okres i WMS dziala w trybie krytycznym z bardzo ograniczonym zakresem operacji.",
      }
    case "suspended":
      return {
        type: "error" as const,
        title: "Licencja zawieszona",
        description:
          "Licencja zostala zawieszona administracyjnie. Czesc operacji write jest blokowana do czasu wznowienia.",
      }
    default:
      return null
  }
}

export default function LicenseAlertBanner() {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const { data } = useQuery({
    queryKey: ["license", "summary"],
    queryFn: LicenseService.getSummary,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  if (!data?.enforcementEnabled) return null

  const copy = getBannerCopy(data.status)
  if (!copy) return null

  return (
    <Box mb={4}>
      <Alert status={copy.type} borderRadius="xl" alignItems="start">
        <AlertIcon mt={1} />
        <Box>
          <AlertTitle>{copy.title}</AlertTitle>
          <AlertDescription>
            {copy.description}
            {isSuperAdmin(currentUser) && (
              <>
                {" "}
                <Link
                  as={RouterLink}
                  to="/admin/license-status"
                  textDecoration="underline"
                >
                  Zobacz szczegoly licencji
                </Link>
                .
              </>
            )}
          </AlertDescription>
        </Box>
      </Alert>
    </Box>
  )
}
